import { get } from 'lodash';
import { useQuasar } from 'quasar';
import { notifyNegative, notifySuccess, useDialog } from 'src/design-system';
import { useOfflineMode as useOfflineSync } from 'src/composables/offlineMode';
import { BROADCAST_MESSAGES } from 'src/constants/broadcastChannel';
import UsersDB from 'src/fb/users-db';
import { auth as authStore } from 'src/store/auth';
import { broadcastChannel as broadcastChannelStore } from 'src/store/broadcastChannel';
import { clients as clientsStore } from 'src/store/clients';
import { network as networkStore } from 'src/store/network';
import { decryptPrivateKey, readSecret } from 'src/utils/CryptoUtils';
import { logEvent } from 'src/utils/LogEvent';
import { clearRedundantOfflineData, deleteDecryptionKey } from 'src/utils/OfflineAccessUtils';
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

// eslint-disable-next-line import/prefer-default-export
export function useOfflineMode() {
  const $q = useQuasar();
  const { t } = useI18n();
  const { alertDialog, promptDialog } = useDialog();
  const { syncToOnline } = useOfflineSync();
  const auth = authStore();
  const broadcastChannel = broadcastChannelStore();
  const clients = clientsStore();
  const network = networkStore();

  // ── Banner visibility ─────────────────────────────────────────────────────

  const warningVisible = ref(false);
  const successVisible = ref(false);

  const enableOfflineModeDialog = computed(() => {
    const role = get(auth.userDetails, 'role');
    const locked = get(auth.lockedPrivileges, [role, 'offline/hasAccess'], false);
    const defaultPriv = get(auth.defaultPrivileges, [role, 'offline/hasAccess'], false);
    const hasAccess = locked ? defaultPriv : get(auth.userPrivileges, 'offline/hasAccess', defaultPriv);
    return !!(hasAccess && auth.userSettings?.offlineClients?.length);
  });

  watch(() => network.networkOnline, (online) => {
    if (!online && !network.offlineMode && enableOfflineModeDialog.value) {
      warningVisible.value = true;
      successVisible.value = false;
    } else if (online) {
      warningVisible.value = false;
    }
  });

  watch([() => network.networkOnline, () => network.offlineMode], ([online, offline]) => {
    if (online && offline) {
      successVisible.value = true;
      warningVisible.value = false;
    } else if (!online && offline) {
      warningVisible.value = false;
    } else if (!offline) {
      successVisible.value = false;
    }
  });

  function dismissWarning() {
    warningVisible.value = false;
  }

  function dismissSuccess() {
    successVisible.value = false;
  }

  // ── Mode switching ────────────────────────────────────────────────────────

  async function checkOfflineData() {
    const usersDB = new UsersDB();
    const user = await usersDB.read(auth.userDetails?.userId || '', { forceIndexedDB: true });
    return !!(user);
  }

  async function switchOfflineMode() {
    if (!network.offlineMode && !await checkOfflineData()) {
      alertDialog(
        t('modals.offlineModal.noDownloadedData'),
        t('modals.offlineModal.noDownloadedDataMessage'),
      );
      return;
    }

    if (network.offlineMode) {
      $q.loading.show({ message: t('modals.offlineModal.syncingInProcess') });

      try {
        broadcastChannel.sendBroadcastMessage(BROADCAST_MESSAGES.SYNC_TO_ONLINE_BROADCAST_MESSAGE);
        await syncToOnline();
        await deleteDecryptionKey();
      } catch (err) {
        logEvent(err);
        notifyNegative(t('modals.offlineModal.syncingToOnlineError', { message: err.message }));
        return;
      } finally {
        $q.loading.hide();
        broadcastChannel.sendBroadcastMessage(BROADCAST_MESSAGES.RELOAD_PAGE_BROADCAST_MESSAGE);
        auth.startSessionTimer();
      }
    } else {
      await clearRedundantOfflineData();
    }

    network.offlineModeSetStatus(!network.offlineMode);
    network.setSwitchingModesStatus();
    await nextTick();

    if (network.offlineMode) {
      const availablePersons = clients.clients.filter(
        (client) => client.status === 'active' && auth.userSettings?.offlineClients?.includes(client.id),
      );

      if (!availablePersons.some(({ id }) => id === auth.currentClient?.id)) {
        auth.setCurrentClient({});
        sessionStorage.removeItem('selectedClient');
      }
    }

    notifySuccess(network.offlineMode
      ? t('modals.offlineModal.setOfflineModeSuccess')
      : t('modals.offlineModal.setOnlineModeSuccess'));

    if (process.env.ENV_TYPE !== 'development') {
      window.location.reload();
    } else if (network.offlineMode) {
      let decryptionKey = await readSecret('decryptionKey');
      while (!decryptionKey) {
        // eslint-disable-next-line no-await-in-loop
        const password = await new Promise((resolve) => {
          promptDialog(
            t('modals.encryptionModal.decryptionPasswordTitle'),
            t('modals.encryptionModal.decryptionSubtitle'),
            resolve,
            { type: 'password', isValid: (val) => !!val },
            // No Quasar-style `cancel: false` in M3; resolve empty so the enclosing
            // loop re-prompts rather than hanging on the unresolved promise.
            () => resolve(''),
          );
        });
        // eslint-disable-next-line no-await-in-loop
        const ok = await decryptPrivateKey(password);
        if (ok) {
          decryptionKey = true;
        } else {
          notifyNegative(t('login.notifyWrongDecryptionPasswordError'));
        }
      }
    } else {
      window.location.reload();
    }
  }

  return {
    enableOfflineModeDialog,
    warningVisible,
    successVisible,
    dismissWarning,
    dismissSuccess,
    switchOfflineMode,
  };
}
